import { Alert, useTheme } from "@mui/material";
import * as React from "react";

import { AutocompleteType } from "@/shared/components/TagAndMetadataAutocomplete";
import { TagInput, Tags, TagsGroup } from "@/shared/components/tags";
import { Device } from "@/shared/domain/devices";
import { useUpdateDevice } from "@/shared/domain/devices/hooks/useUpdateDevice";

interface UpdateDeviceTagsProps {
  device: Device;
  resourceOwnerId: string;
  readonly?: boolean;
  onTagsUpdated?: (device: Device) => void;
  onError?: (error: Error) => void;
}

export const UpdateDeviceTags: React.FC<UpdateDeviceTagsProps> = ({
  device,
  resourceOwnerId,
  readonly = false,
  onTagsUpdated,
  onError,
}) => {
  const theme = useTheme();
  const updateDeviceMutation = useUpdateDevice();

  const tagAdded = (tag: string) => {
    updateDeviceMutation.mutate(
      {
        deviceId: device.device_id,
        resourceOwnerId,
        updates: {
          metadata_changeset: {
            put_tags: [tag],
          },
        },
      },
      {
        onSuccess: (updatedDevice) => {
          if (onTagsUpdated) {
            onTagsUpdated(updatedDevice);
          }
        },
        onError: (error: Error) => {
          if (onError) {
            onError(error);
          }
        },
      },
    );
  };

  const tagRemoved = (tag: string) => {
    updateDeviceMutation.mutate(
      {
        deviceId: device.device_id,
        resourceOwnerId,
        updates: {
          metadata_changeset: {
            remove_tags: [tag],
          },
        },
      },
      {
        onSuccess: (updatedDevice) => {
          if (onTagsUpdated) {
            onTagsUpdated(updatedDevice);
          }
        },
        onError: (error: Error) => {
          if (onError) {
            onError(error);
          }
        },
      },
    );
  };

  return (
    <>
      {readonly && device.tags.length === 0 && <>None</>}
      <TagsGroup style={{ marginTop: theme.spacing(1) }}>
        <Tags
          key={device.device_id}
          tags={device.tags}
          onDeleteTag={readonly ? undefined : tagRemoved}
        />
        {readonly === false && (
          <TagInput
            key={`${device.device_id}-input`}
            autocompleteType={AutocompleteType.GenericTags}
            onAddTag={tagAdded}
            tags={device.tags}
            size="small"
            isLoading={updateDeviceMutation.isPending}
          />
        )}
      </TagsGroup>

      {updateDeviceMutation.isError && (
        <Alert
          onClose={() => updateDeviceMutation.reset()}
          severity="error"
          sx={{ marginTop: theme.spacing(1) }}
        >
          {updateDeviceMutation.error.message}
        </Alert>
      )}
    </>
  );
};
